import { CheckCircleOutlineRounded, CircleOutlined, ThumbUpOutlined } from "@mui/icons-material";
import { Button, Link, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { Links } from "src/constants";
import { useSession } from "src/hooks";
import { useReviewFormSubmission, type FormSubmissionDetailDto } from "src/http";
import { ItemProperty } from "../itemProperties";

interface Props {
  formSubmission: FormSubmissionDetailDto;
}

const FormSubmissionReviewedProperty = (props: Props) => {
  const sessionUser = useSession();
  const { mutate: markAsReviewed, isLoading } = useReviewFormSubmission();

  const isRecipient = props.formSubmission.recipientUser?.userId === sessionUser.userId;
  const isReviewed = props.formSubmission.reviewedByRecipientDateTimeUtc != null;

  function handleReviewClicked() {
    if (!isRecipient || isReviewed) {
      return;
    }

    markAsReviewed({
      formSubmissionGuid: props.formSubmission.formSubmissionGuid,
    });
  }

  return (
    <ItemProperty labelIcon={<ThumbUpOutlined />} label="Reviewed">
      {isRecipient ? (
        <Stack spacing={0.25}>
          <Button
            variant="tertiary"
            onClick={handleReviewClicked}
            startIcon={isReviewed ? <CheckCircleOutlineRounded /> : <CircleOutlined />}
            disabled={isLoading || isReviewed}
            sx={{ alignSelf: "start" }}
          >
            {isReviewed ? "Reviewed" : "Mark Reviewed"}
          </Button>
          {isReviewed && (
            <Typography variant="caption">
              Marked reviewed on{" "}
              {DateTime.fromISO(props.formSubmission.reviewedByRecipientDateTimeUtc!, { zone: "utc" })
                .toLocal()
                .toLocaleString(DateTime.DATE_MED)}
            </Typography>
          )}
        </Stack>
      ) : (
        <>
          {props.formSubmission.recipientUser && props.formSubmission.reviewedByRecipientDateTimeUtc ? (
            <Stack direction="row" spacing={0.25}>
              <CheckCircleOutlineRounded sx={{ alignSelf: "start", position: "relative", top: 3 }} />
              <Stack spacing={-0.25}>
                <Typography>
                  By{" "}
                  <Link href={Links.User(props.formSubmission.recipientUser.userId)}>
                    {props.formSubmission.recipientUser.displayName}
                  </Link>
                </Typography>
                <Typography variant="caption">
                  Marked reviewed on{" "}
                  {DateTime.fromISO(props.formSubmission.reviewedByRecipientDateTimeUtc, { zone: "utc" })
                    .toLocal()
                    .toLocaleString(DateTime.DATE_MED)}
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <Typography variant="placeholder">Not reviewed yet.</Typography>
          )}
        </>
      )}
    </ItemProperty>
  );
};

export { FormSubmissionReviewedProperty };
